import { cn } from "@/lib/utils"
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react"

type ChatContainerContextType = {
  isAtBottom: boolean
  scrollToBottom: () => void
}

const ChatContainerContext = createContext<ChatContainerContextType>({
  isAtBottom: true,
  scrollToBottom: () => {},
})

export function useChatContainer() {
  return useContext(ChatContainerContext)
}

export type ChatContainerRootProps = {
  children: React.ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

export type ChatContainerContentProps = {
  children: React.ReactNode
  className?: string
} & React.HTMLAttributes<HTMLDivElement>

export type ChatContainerScrollAnchorProps = {
  className?: string
  ref?: React.RefObject<HTMLDivElement>
} & React.HTMLAttributes<HTMLDivElement>

function ChatContainerRoot({
  children,
  className,
  ...props
}: ChatContainerRootProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  const [isAtBottom, setIsAtBottom] = useState(true)
  const wasAtBottomRef = useRef(true)

  const scrollToBottom = useCallback(() => {
    anchorRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handleScroll = () => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40
      setIsAtBottom(atBottom)
      wasAtBottomRef.current = atBottom
    }

    el.addEventListener("scroll", handleScroll, { passive: true })
    return () => el.removeEventListener("scroll", handleScroll)
  }, [])

  // Auto-scroll when content changes if already at bottom
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new MutationObserver(() => {
      if (wasAtBottomRef.current) {
        anchorRef.current?.scrollIntoView({ behavior: "smooth" })
      }
    })

    observer.observe(el, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [])

  return (
    <ChatContainerContext.Provider value={{ isAtBottom, scrollToBottom }}>
      <div
        ref={containerRef}
        className={cn("flex overflow-y-auto", className)}
        role="log"
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child) && child.type === ChatContainerScrollAnchor) {
            return React.cloneElement(child as React.ReactElement<{ ref?: React.Ref<HTMLDivElement> }>, {
              ref: anchorRef,
            })
          }
          return child
        })}
      </div>
    </ChatContainerContext.Provider>
  )
}

function ChatContainerContent({
  children,
  className,
  ...props
}: ChatContainerContentProps) {
  return (
    <div className={cn("flex w-full flex-col", className)} {...props}>
      {children}
    </div>
  )
}

const ChatContainerScrollAnchor = React.forwardRef<
  HTMLDivElement,
  ChatContainerScrollAnchorProps
>(({ className, ...props }, ref) => {
  return (
    <div
      ref={ref}
      className={cn("h-px w-full shrink-0 scroll-mt-4", className)}
      aria-hidden="true"
      {...props}
    />
  )
})
ChatContainerScrollAnchor.displayName = "ChatContainerScrollAnchor"

export { ChatContainerRoot, ChatContainerContent, ChatContainerScrollAnchor }
